// Visual docking for parsed connections (Armin, Slack): the model records a logical
// connection (a `touches_connection` edge) when a pipe line's end lands near an object,
// but the stored geometry can leave a small gap. This closes that gap PURELY VISUALLY —
// it snaps a connected line's rendered endpoint onto the object it joins, matching how
// "Add Object" glues drawn lines. Stored geometry_wkt is never mutated.
//
// Pure + DOM-free so it's unit-testable and shared with the renderer. Reuses the same
// shape geometry the connection hit-test/snap already uses (shapeSnap).

import { nearestPointOnPath, type Pt } from './shapeSnap'

/** Minimal shape a line can dock onto — a box, a traced polygon, a junction dot, or another line. */
export interface DockShape {
  id: string
  x: number
  y: number
  width: number
  height: number
  /** Freeform polygon outline (ENG-2582); when present the ring is the vertices. */
  vertices?: Pt[]
  /** A T/junction dot — dock onto its center, not a perimeter. */
  isJunction?: boolean
  /** A connection line's own polyline points (for line-on-line docking). */
  linePoints?: Pt[]
}

/** Don't move an endpoint farther than this (image px) — guards against a spurious
 *  edge yanking a line across the drawing. Real detector gaps are a handful of px. */
export const DEFAULT_MAX_DOCK_DIST = 80

/** The point on `shape` a line end approaching from `from` should dock to. */
export function dockPointOnShape(shape: DockShape, from: Pt): Pt {
  if (shape.isJunction) {
    return { x: shape.x + shape.width / 2, y: shape.y + shape.height / 2 }
  }
  if (shape.vertices && shape.vertices.length >= 3) {
    return nearestPointOnPath(from, shape.vertices, true).point
  }
  if (shape.linePoints && shape.linePoints.length >= 2) {
    return nearestPointOnPath(from, shape.linePoints, false).point
  }
  // Box: nearest point on the bbox perimeter.
  const ring: Pt[] = [
    { x: shape.x, y: shape.y },
    { x: shape.x + shape.width, y: shape.y },
    { x: shape.x + shape.width, y: shape.y + shape.height },
    { x: shape.x, y: shape.y + shape.height },
  ]
  return nearestPointOnPath(from, ring, true).point
}

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y)

/**
 * Return `pts` with its two ENDS docked onto the objects the line connects to.
 * Each connected object is assigned to whichever end (start/last) is nearer, and that
 * end is moved onto the object's dock point — unless the move exceeds `maxDockDist`.
 * Never moves interior points; never collapses the line (a shape nearest to both ends
 * docks only the nearer end). Returns the same array reference when nothing changes.
 */
export function dockConnectionEndpoints(
  pts: Pt[],
  connected: DockShape[],
  maxDockDist: number = DEFAULT_MAX_DOCK_DIST,
): Pt[] {
  if (pts.length < 2 || connected.length === 0) return pts
  const startIdx = 0
  const lastIdx = pts.length - 1

  // The nearest in-range shape to a given end, chosen INDEPENDENTLY per end. Picking
  // per end (rather than pre-assigning each shape to its closer end) means a 2-object
  // line always docks both ends to their respective nearest object, even when one
  // object happens to sit marginally closer to the other end.
  const bestFor = (idx: number): { shape: DockShape; p: Pt; d: number } | null => {
    let best: { shape: DockShape; p: Pt; d: number } | null = null
    for (const shape of connected) {
      const p = dockPointOnShape(shape, pts[idx])
      const d = dist(pts[idx], p)
      if (d <= maxDockDist && (!best || d < best.d)) best = { shape, p, d }
    }
    return best
  }

  let startBest = bestFor(startIdx)
  let lastBest = bestFor(lastIdx)

  // If BOTH ends want the same shape (a short stub next to one object), dock only the
  // nearer end — snapping both ends onto one shape would collapse the line to a point.
  if (startBest && lastBest && startBest.shape === lastBest.shape) {
    if (startBest.d <= lastBest.d) lastBest = null
    else startBest = null
  }
  if (!startBest && !lastBest) return pts

  const out = pts.slice()
  if (startBest) out[startIdx] = startBest.p
  if (lastBest) out[lastIdx] = lastBest.p
  return out
}
